import type {
  OrganizationCommunications,
  OrganizationCommunicationsPrerequisites,
} from './organization-onboarding-contracts'

export type SetupPrerequisite =
  'clinicInfo' | 'workingHours' | 'services' | 'booking'
export type SetupPrerequisiteStatuses =
  OrganizationCommunicationsPrerequisites['prerequisites']
export type SetupSettingsSection = 'clinic' | 'hours' | 'services'

// Presentation of the canonical prerequisite groups only. The backend owns
// readiness and enforces it before any provisioning step.
export const setupPrerequisites: Record<
  SetupPrerequisite,
  {
    label: string
    detail: string
    section: SetupSettingsSection
    voiceOnly: boolean
  }
> = {
  clinicInfo: {
    label: 'Clinic Info',
    detail: 'Clinic name, address, phone and email.',
    section: 'clinic',
    voiceOnly: false,
  },
  workingHours: {
    label: 'Working Hours',
    detail: 'Time zone and weekly hours.',
    section: 'hours',
    voiceOnly: false,
  },
  services: {
    label: 'Services',
    detail: 'At least one available service.',
    section: 'services',
    voiceOnly: true,
  },
  booking: {
    label: 'Booking',
    detail:
      'At least one service marked Available for Flio booking, with its appointment type.',
    section: 'services',
    voiceOnly: true,
  },
}

export const setupPrerequisiteOrder: SetupPrerequisite[] = [
  'clinicInfo',
  'workingHours',
  'services',
  'booking',
]

export function voiceCapability(
  capability: OrganizationCommunications['capability']
) {
  return capability === 'VOICE_ONLY' || capability === 'VOICE_AND_TEXT'
}

// The prerequisites still missing for these capabilities. Voice also needs
// Services and Booking; Text only never does.
export function missingSetupPrerequisites(
  statuses: SetupPrerequisiteStatuses,
  voice: boolean
): SetupPrerequisite[] {
  return setupPrerequisiteOrder.filter(
    (key) =>
      statuses[key] === 'REQUIRED' &&
      (voice || !setupPrerequisites[key].voiceOnly)
  )
}

export function setupPrerequisiteCopy(reason: string | null) {
  if (reason === 'CLINIC_INFO_AND_WORKING_HOURS_REQUIRED')
    return 'Complete Clinic Info, including the clinic phone and email, and Working Hours in Settings before setting up phone & texting.'
  if (reason === 'CLINIC_INFO_REQUIRED')
    return 'Complete Clinic Info, including the clinic phone and email, in Settings before setting up phone & texting.'
  if (reason === 'WORKING_HOURS_REQUIRED')
    return 'Complete Working Hours in Settings before setting up phone & texting.'
  if (reason === 'NEXHEALTH_WORKSPACE_EVIDENCE_REQUIRED')
    return 'Current NexHealth workspace evidence is unavailable. Review the clinic connection before continuing setup.'
  if (reason === 'SERVICES_AND_BOOKING_REQUIRED')
    return 'Voice requires Services and Booking. Add a service in Settings and mark it Available for Flio booking, or set up Text only.'
  if (reason === 'SERVICES_REQUIRED')
    return 'Voice requires at least one available service in Settings, or set up Text only.'
  if (reason === 'BOOKING_REQUIRED')
    return 'Voice requires Booking: mark at least one service Available for Flio booking in Settings, or set up Text only.'
  return 'Complete this clinic’s setup prerequisites before setting up phone & texting.'
}

// Presentation of the existing canonical states only; this grants no authority.
export function communicationsSetupCopy(state: OrganizationCommunications) {
  switch (state.status) {
    case 'SETUP_REQUIRED':
      return setupPrerequisiteCopy(state.reason)
    case 'NOT_CONFIGURED':
      return 'Choose phone/texting capabilities and authorize an existing phone number in setup. No number or texting capability is enabled automatically.'
    case 'PROVISIONING':
      return 'Phone & texting provisioning is in progress. Continue the existing setup to check progress.'
    case 'VALIDATING':
      return 'Phone & texting validation is in progress.'
    case 'READY':
      return 'Phone & texting setup is complete.'
    case 'PARTIAL':
    case 'BLOCKED':
      return 'Phone & texting setup needs attention. Continue the existing setup to review the recovery steps.'
  }
}
